/**
 * DealImportPreviewDialog — preview premium e seguro de uma importação de negócios.
 *
 * Reusa ImportPreviewShell, ImportPreviewMetricCard, ImportPreviewStatusDot
 * e ImportPreviewErrorTooltip. Dados servidos pela RPC `get_deal_import_preview`
 * (limite 50, telefone e e-mail mascarados no servidor).
 *
 * RELEASE:
 *   2026-05-17 v1.1.0 - Exibe todas as colunas relevantes do arquivo importado
 *                       (Email, Título, Valor, Fase, Origem, Tags) + tooltip de
 *                       motivo do erro. Tabela com scroll horizontal.
 *   2026-05-17 v1.0.0 - Versão inicial.
 */
import { useMemo } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Briefcase,
  Users,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Inbox,
} from 'lucide-react';

import { useDealImportPreview } from '@/hooks/useDealImportPreview';
import { ImportPreviewShell } from '@/components/imports/shared/ImportPreviewShell';
import { ImportPreviewMetricCard } from '@/components/imports/shared/ImportPreviewMetricCard';
import { ImportPreviewErrorTooltip } from '@/components/imports/shared/ImportPreviewErrorTooltip';

// Mapa de status (deal_import_item_status)
const STATUS_DOT: Record<string, string> = {
  pending: 'bg-muted-foreground',
  valid: 'bg-blue-500',
  invalid: 'bg-amber-500',
  duplicate: 'bg-amber-500',
  blacklisted: 'bg-red-500',
  processing: 'bg-blue-500',
  succeeded: 'bg-emerald-500',
  failed: 'bg-red-500',
  skipped: 'bg-muted-foreground',
};

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pendente',
  valid: 'Válido',
  invalid: 'Inválido',
  duplicate: 'Duplicado',
  blacklisted: 'Blacklist',
  processing: 'Processando',
  succeeded: 'Sucesso',
  failed: 'Falha',
  skipped: 'Ignorado',
};

export interface DealImportJobSummary {
  id: string;
  name: string;
  created_at?: string;
  total_rows?: number;
  succeeded_items?: number;
  failed_items?: number;
  invalid_count?: number;
  duplicate_count?: number;
  blacklisted_count?: number;
}

interface DealImportPreviewDialogProps {
  job: DealImportJobSummary;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Formata valor monetário no padrão pt-BR usando a moeda informada (fallback BRL). */
function formatCurrency(value: number | null, currency: string | null) {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  try {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency || 'BRL',
    }).format(value);
  } catch {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  }
}

/** Divide o campo `tags` (separadores comuns) e retorna até 3 chips + contador. */
function splitTags(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(/[;,|]/)
    .map((t) => t.trim())
    .filter(Boolean);
}

export default function DealImportPreviewDialog({
  job,
  open,
  onOpenChange,
}: DealImportPreviewDialogProps) {
  const { data: items, isLoading } = useDealImportPreview(job.id, open);

  const invalidTotal = useMemo(
    () =>
      (job.invalid_count ?? 0) + (job.duplicate_count ?? 0) + (job.blacklisted_count ?? 0),
    [job],
  );

  return (
    <ImportPreviewShell
      open={open}
      onOpenChange={onOpenChange}
      headerIcon={<Briefcase className="h-4 w-4" />}
      title={job.name}
      createdAt={job.created_at}
      metrics={
        <>
          <ImportPreviewMetricCard
            title="Total"
            value={job.total_rows ?? 0}
            icon={<Users className="h-4 w-4" />}
            variant="primary"
          />
          <ImportPreviewMetricCard
            title="Sucesso"
            value={job.succeeded_items ?? 0}
            icon={<CheckCircle2 className="h-4 w-4" />}
            variant="success"
          />
          <ImportPreviewMetricCard
            title="Inválidos"
            value={invalidTotal}
            icon={<AlertTriangle className="h-4 w-4" />}
            variant="info"
          />
          <ImportPreviewMetricCard
            title="Falhas"
            value={job.failed_items ?? 0}
            icon={<XCircle className="h-4 w-4" />}
            variant="danger"
          />
        </>
      }
    >
      <div className="space-y-2">
        <h3 className="text-sm font-semibold">Amostra de negócios importados</h3>

        {isLoading ? (
          <div className="rounded-md border overflow-hidden">
            <div className="space-y-px">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-10 w-full rounded-none" />
              ))}
            </div>
          </div>
        ) : !items || items.length === 0 ? (
          <div className="rounded-md border border-dashed py-10 flex flex-col items-center justify-center gap-2 text-muted-foreground">
            <Inbox className="h-8 w-8 opacity-40" />
            <p className="text-sm">Nenhum item disponível para preview.</p>
          </div>
        ) : (
          // Container com scroll vertical (linhas) + horizontal (colunas).
          <div className="rounded-md border overflow-hidden">
            <div className="max-h-[460px] overflow-auto">
              <Table className="min-w-[1280px]">
                <TableHeader className="sticky top-0 z-10 bg-muted/60 backdrop-blur supports-[backdrop-filter]:bg-muted/50">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="h-9 w-10 text-xs">#</TableHead>
                    <TableHead className="h-9 text-xs w-[160px]">Nome</TableHead>
                    <TableHead className="h-9 text-xs w-[150px]">Telefone</TableHead>
                    <TableHead className="h-9 text-xs w-[170px]">Email</TableHead>
                    <TableHead className="h-9 text-xs w-[180px]">Título</TableHead>
                    <TableHead className="h-9 text-xs w-[110px] text-right">Valor</TableHead>
                    <TableHead className="h-9 text-xs w-[120px]">Pipeline</TableHead>
                    <TableHead className="h-9 text-xs w-[120px]">Fase</TableHead>
                    <TableHead className="h-9 text-xs w-[140px]">Vendedor</TableHead>
                    <TableHead className="h-9 text-xs w-[110px]">Origem</TableHead>
                    <TableHead className="h-9 text-xs w-[180px]">Tags</TableHead>
                    <TableHead className="h-9 text-xs w-[110px]">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => {
                    const tagsList = splitTags(it.tags);
                    const formattedValue = formatCurrency(it.value, it.currency);
                    return (
                      <TableRow key={it.id} className="text-xs">
                        <TableCell className="py-2 text-muted-foreground tabular-nums">
                          {it.row_index}
                        </TableCell>
                        <TableCell className="py-2 font-medium max-w-[160px] truncate">
                          {it.name || <span className="text-muted-foreground/60">—</span>}
                        </TableCell>
                        <TableCell className="py-2 font-mono tabular-nums whitespace-nowrap">
                          {it.phone_masked || (
                            <span className="text-muted-foreground/60">—</span>
                          )}
                        </TableCell>
                        <TableCell className="py-2 text-muted-foreground max-w-[170px] truncate">
                          {it.email_masked || (
                            <span className="text-muted-foreground/60">—</span>
                          )}
                        </TableCell>
                        <TableCell className="py-2 max-w-[180px] truncate" title={it.title ?? ''}>
                          {it.title || <span className="text-muted-foreground/60">—</span>}
                        </TableCell>
                        <TableCell className="py-2 text-right tabular-nums whitespace-nowrap">
                          {formattedValue || (
                            <span className="text-muted-foreground/60">—</span>
                          )}
                        </TableCell>
                        <TableCell className="py-2 text-muted-foreground max-w-[120px] truncate">
                          {it.pipeline_name || (
                            <span className="text-muted-foreground/60">—</span>
                          )}
                        </TableCell>
                        <TableCell className="py-2 text-muted-foreground max-w-[120px] truncate">
                          {it.phase_name || (
                            <span className="text-muted-foreground/60">—</span>
                          )}
                        </TableCell>
                        <TableCell className="py-2 text-muted-foreground max-w-[140px] truncate">
                          {it.seller_name || (
                            <span className="text-muted-foreground/60">—</span>
                          )}
                        </TableCell>
                        <TableCell className="py-2 text-muted-foreground max-w-[110px] truncate">
                          {it.source || <span className="text-muted-foreground/60">—</span>}
                        </TableCell>
                        <TableCell className="py-2 max-w-[180px]">
                          {tagsList.length === 0 ? (
                            <span className="text-muted-foreground/60">—</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {tagsList.slice(0, 3).map((t, idx) => (
                                <Badge
                                  key={`${t}-${idx}`}
                                  variant="outline"
                                  className="h-5 px-1.5 text-[10px] font-normal"
                                >
                                  {t}
                                </Badge>
                              ))}
                              {tagsList.length > 3 && (
                                <Badge
                                  variant="outline"
                                  className="h-5 px-1.5 text-[10px] font-normal"
                                >
                                  +{tagsList.length - 3}
                                </Badge>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="py-2">
                          <ImportPreviewErrorTooltip
                            colorClass={STATUS_DOT[it.status] ?? 'bg-muted-foreground'}
                            label={STATUS_LABEL[it.status] ?? it.status}
                            errorMessage={it.error_message}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </ImportPreviewShell>
  );
}
